<template>
  <div class="product-form">
    <page-loading v-if="loading" message="Carregando produto" />
    <template v-else>
      <page-header
        :title="isEdit ? 'Editar produto' : 'Novo produto'"
        :subtitle="isEdit ? 'Atualize as informações do seu catálogo' : 'Cadastre um item para vender no PDV'"
        bordered
      >
        <template #leading>
          <div class="product-form__header-icon">
            <v-icon color="primary">mdi-package-variant</v-icon>
          </div>
        </template>
      </page-header>

      <v-form ref="form" v-model="valid">
        <v-row>
          <v-col cols="12" lg="8">
            <form-section title="Informações do produto" icon="mdi-package-variant" compact>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.name"
                    label="Nome *"
                    class="br-form-field"
                    :rules="[v => !!v || 'Obrigatório']"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.barcode"
                    label="Código de barras"
                    class="br-form-field"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.categoryId"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    label="Categoria"
                    class="br-form-field"
                    outlined
                    dense
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.unitMeasure"
                    :items="unitOptions"
                    label="Unidade *"
                    class="br-form-field"
                    outlined
                    dense
                    :rules="[v => !!v || 'Obrigatório']"
                  />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.description"
                    label="Descrição"
                    class="br-form-field"
                    outlined
                    dense
                    rows="2"
                  />
                </v-col>
              </v-row>
            </form-section>

            <form-section title="Preços" icon="mdi-currency-brl" compact>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.costPrice"
                    label="Preço de custo"
                    class="br-form-field"
                    outlined
                    dense
                    v-money="money"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.salePrice"
                    label="Preço de venda *"
                    class="br-form-field"
                    outlined
                    dense
                    v-money="money"
                    :rules="[v => !!v || 'Obrigatório']"
                  />
                </v-col>
              </v-row>
            </form-section>

            <form-section title="Estoque e disponibilidade" icon="mdi-warehouse" compact>
              <v-row dense>
                <template v-if="!isEdit">
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="form.stockQuantity"
                      label="Estoque inicial"
                      type="number"
                      min="0"
                      outlined
                      dense
                      class="br-form-field"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="form.minimumStock"
                      label="Estoque mínimo"
                      type="number"
                      min="0"
                      outlined
                      dense
                      class="br-form-field"
                      hint="Alerta quando o estoque atingir este valor"
                      persistent-hint
                    />
                  </v-col>
                </template>
                <template v-else>
                  <v-col cols="12" md="4">
                    <v-text-field
                      :value="`${currentStock} ${form.unitMeasure}`"
                      label="Estoque atual"
                      outlined
                      dense
                      readonly
                      class="br-form-field"
                      hint="Use a aba Estoque em Produtos para movimentar"
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="form.minimumStock"
                      label="Estoque mínimo"
                      type="number"
                      min="0"
                      outlined
                      dense
                      class="br-form-field"
                    />
                  </v-col>
                </template>
                <v-col cols="12" md="auto" class="product-form__switch-col">
                  <v-switch v-model="form.isActive" label="Produto ativo" hide-details color="primary" />
                </v-col>
              </v-row>
            </form-section>

            <form-section title="Cashback" icon="mdi-gift-outline" compact>
              <v-row dense>
                <v-col cols="12" md="4" class="product-form__switch-col">
                  <v-switch
                    v-model="form.noInstantCashback"
                    label="Não tem cashback direto"
                    hide-details
                    color="primary"
                    @change="onNoCashbackChange"
                  />
                </v-col>
                <v-col
                  v-if="!form.noInstantCashback"
                  cols="12"
                  md="8"
                  class="product-form__slider-col"
                >
                  <label class="br-field-label">Cashback direto</label>
                  <v-slider
                    v-model="form.instantCashbackPercent"
                    min="0"
                    max="50"
                    hide-details
                    thumb-color="primary"
                    track-fill-color="primary"
                    track-color="#f57990"
                  >
                    <template v-slot:append>
                      <span class="product-form__slider-value">{{ form.instantCashbackPercent }}%</span>
                    </template>
                  </v-slider>
                  <p v-if="partnerDefaultCashback > 0" class="product-form__hint">
                    Padrão do parceiro: {{ partnerDefaultCashback }}%
                  </p>
                </v-col>
              </v-row>
            </form-section>
          </v-col>

          <v-col cols="12" lg="4">
            <aside class="product-form__aside">
              <form-section title="Imagem" icon="mdi-image-outline" compact>
                <div
                  class="product-form__dropzone"
                  :class="{ 'product-form__dropzone--filled': imagePreview }"
                  @click="triggerImagePick"
                >
                  <template v-if="imagePreview">
                    <img :src="imagePreview" alt="Prévia do produto" class="product-form__image" />
                    <v-btn
                      icon
                      small
                      class="product-form__image-remove"
                      @click.stop="removeImage"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <template v-else>
                    <div class="product-form__dropzone-icon">
                      <v-icon size="28" color="primary">mdi-camera-plus-outline</v-icon>
                    </div>
                    <p class="product-form__dropzone-title">Adicionar imagem</p>
                    <p class="product-form__dropzone-hint">PNG ou JPG, opcional</p>
                  </template>
                </div>
                <v-file-input
                  ref="imageInput"
                  v-model="imageFile"
                  accept="image/*"
                  class="product-form__file-input"
                  hide-input
                  prepend-icon=""
                  @change="onImageSelected"
                />
              </form-section>

              <div class="product-form__preview">
                <p class="product-form__preview-label">Prévia no PDV</p>
                <div class="product-form__preview-card">
                  <div class="product-form__preview-thumb">
                    <img v-if="imagePreview" :src="imagePreview" :alt="form.name || 'Produto'" />
                    <v-icon v-else size="32" color="primary">mdi-package-variant-closed</v-icon>
                  </div>
                  <p class="product-form__preview-name">{{ form.name || "Nome do produto" }}</p>
                  <p class="product-form__preview-price">{{ form.salePrice || "R$ 0,00" }}</p>
                  <span
                    v-if="!form.noInstantCashback && form.instantCashbackPercent > 0"
                    class="product-form__preview-cashback"
                  >
                    {{ form.instantCashbackPercent }}% cashback
                  </span>
                </div>
              </div>
            </aside>
          </v-col>
        </v-row>

        <div class="br-sticky-actions">
          <v-btn text class="product-form__cancel" @click="$router.push('/produtos')">Cancelar</v-btn>
          <v-spacer />
          <v-btn
            dark
            color="primary"
            depressed
            large
            :loading="saving"
            :disabled="!valid"
            @click="save"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Salvar produto
          </v-btn>
        </div>
      </v-form>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import FormSection from "@/components/ui/FormSection.vue";
import PageHeader from "@/components/ui/PageHeader.vue";
import { pdvApi } from "@/services/pdv/pdvApi";
import { GetPartner } from "@/services/partners/GetPartner";
import { CatalogProductType } from "@/types/pdv/Pdv";

export default defineComponent({
  components: { FormSection, PageHeader, PageLoading },
  data() {
    return {
      valid: false,
      saving: false,
      loading: false,
      imageFile: null as File | null,
      imagePreview: "",
      imageBase64: "",
      clearImage: false,
      partnerDefaultCashback: 0,
      categories: [] as Array<{ id: string; name: string }>,
      unitOptions: ["UN", "KG", "LT", "MT"],
      form: {
        name: "",
        barcode: "",
        categoryId: "",
        description: "",
        costPrice: "R$ 0,00",
        salePrice: "R$ 0,00",
        unitMeasure: "UN",
        stockQuantity: 0,
        minimumStock: 0,
        isActive: true,
        instantCashbackPercent: 0,
        noInstantCashback: false,
      },
      money: { decimal: ",", thousands: ".", prefix: "R$ ", precision: 2 },
    };
  },
  computed: {
    isEdit(): boolean {
      return !!this.$route.params.id && this.$route.params.id !== "new";
    },
    currentStock(): number {
      return Number(this.form.stockQuantity) || 0;
    },
  },
  watch: {
    "$route.params.id": {
      immediate: false,
      handler() {
        this.loadForm();
      },
    },
  },
  async mounted() {
    await this.loadForm();
  },
  methods: {
    triggerImagePick() {
      const input = this.$refs.imageInput as { $el?: HTMLElement } | undefined;
      const el = input?.$el?.querySelector("input[type='file']") as HTMLInputElement | null;
      el?.click();
    },
    async loadForm() {
      this.loading = true;
      try {
        const [cats, partner] = await Promise.all([
          pdvApi.listCategories({ limit: 100 }),
          new GetPartner().execute(),
        ]);
        this.categories = cats.data || [];
        this.partnerDefaultCashback = Number(partner.partnerConfigs?.instantCashback || 0);

        if (this.isEdit) {
          const product = await pdvApi.getProduct(String(this.$route.params.id));
          this.populateForm(product);
        } else {
          this.resetForm();
          this.form.instantCashbackPercent = this.partnerDefaultCashback;
        }
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao carregar produto",
          color: "error",
          timer: 3000,
        });
        this.$router.push("/produtos");
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.imageFile = null;
      this.imagePreview = "";
      this.imageBase64 = "";
      this.clearImage = false;
      Object.assign(this.form, {
        name: "",
        barcode: "",
        categoryId: "",
        description: "",
        costPrice: "R$ 0,00",
        salePrice: "R$ 0,00",
        unitMeasure: "UN",
        stockQuantity: 0,
        minimumStock: 0,
        isActive: true,
        instantCashbackPercent: 0,
        noInstantCashback: false,
      });
    },
    populateForm(product: CatalogProductType) {
      const noCashback = !!product.noInstantCashback;
      this.imageFile = null;
      this.imageBase64 = "";
      this.clearImage = false;
      this.imagePreview = product.imageUrl || "";
      Object.assign(this.form, {
        name: product.name,
        barcode: product.barcode || "",
        categoryId: product.categoryId || "",
        description: product.description || "",
        costPrice: this.formatMoney(product.costPrice),
        salePrice: this.formatMoney(product.salePrice),
        unitMeasure: this.unitOptions.includes(product.unitMeasure)
          ? product.unitMeasure
          : "UN",
        stockQuantity: Number(product.stockQuantity) || 0,
        minimumStock: Number(product.minimumStock) || 0,
        isActive: product.isActive,
        noInstantCashback: noCashback,
        instantCashbackPercent: noCashback
          ? 0
          : product.instantCashbackPercent != null
            ? Number(product.instantCashbackPercent)
            : this.partnerDefaultCashback,
      });
    },
    onNoCashbackChange(value: boolean) {
      if (!value && this.form.instantCashbackPercent === 0) {
        this.form.instantCashbackPercent = this.partnerDefaultCashback;
      }
    },
    onImageSelected() {
      const file = Array.isArray(this.imageFile) ? this.imageFile[0] : this.imageFile;
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        this.imagePreview = result;
        this.imageBase64 = result.replace(/^data:[^;]+;base64,/, "");
        this.clearImage = false;
      };
      reader.readAsDataURL(file);
    },
    removeImage() {
      this.imageFile = null;
      this.imagePreview = "";
      this.imageBase64 = "";
      this.clearImage = true;
    },
    formatMoney(value: number) {
      return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    parseMoney(value: string) {
      return Number(value.replace("R$", "").replace(/\./g, "").replace(",", ".").trim()) || 0;
    },
    generateAutoSku() {
      const random = Math.random().toString(36).slice(2, 8).toUpperCase();
      return `PDV${Date.now().toString(36).toUpperCase()}${random}`.slice(0, 20);
    },
    async save() {
      this.saving = true;
      try {
        const payload: Record<string, unknown> = {
          name: this.form.name,
          barcode: this.form.barcode || undefined,
          categoryId: this.form.categoryId || undefined,
          description: this.form.description || undefined,
          costPrice: this.parseMoney(this.form.costPrice),
          salePrice: this.parseMoney(this.form.salePrice),
          unitMeasure: this.form.unitMeasure,
          isActive: this.form.isActive,
          noInstantCashback: this.form.noInstantCashback,
        };
        if (!this.form.noInstantCashback) {
          payload.instantCashbackPercent = this.form.instantCashbackPercent;
        }
        if (this.imageBase64) {
          payload.image = this.imageBase64;
        } else if (this.isEdit && this.clearImage) {
          payload.clearImage = true;
        }
        if (this.isEdit) {
          payload.minimumStock = Number(this.form.minimumStock) || 0;
          await pdvApi.updateProduct(String(this.$route.params.id), payload);
        } else {
          payload.sku = this.generateAutoSku();
          payload.stockQuantity = Number(this.form.stockQuantity) || 0;
          payload.minimumStock = Number(this.form.minimumStock) || 0;
          await pdvApi.createProduct(payload);
        }
        this.$store.dispatch("showSnack", { message: "Produto salvo", color: "success", timer: 3000 });
        this.$router.push("/produtos");
      } catch {
        this.$store.dispatch("showSnack", { message: "Erro ao salvar produto", color: "error", timer: 3000 });
      } finally {
        this.saving = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.product-form__header-icon {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, rgba(229, 24, 61, 0.16), rgba(229, 24, 61, 0.06));
}

.product-form__aside {
  position: sticky;
  top: 84px;
}

.product-form__switch-col,
.product-form__slider-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-form__slider-value {
  min-width: 42px;
  text-align: right;
  font-weight: 800;
  color: $br-brand;
}

.product-form__hint {
  margin: 4px 0 0;
  font-size: $br-font-size-xs;
  color: $br-muted;
  font-weight: 600;
}

.product-form__dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  border-radius: $br-radius-lg;
  border: 1.5px dashed rgba(229, 24, 61, 0.28);
  background:
    radial-gradient(circle at 50% 0%, rgba(229, 24, 61, 0.06), transparent 55%),
    $br-bg-subtle;
  cursor: pointer;
  overflow: hidden;
  transition: border-color $br-transition-fast, box-shadow $br-transition-fast,
    transform $br-transition-fast;

  &:hover {
    border-color: $br-brand;
    box-shadow: 0 8px 20px rgba(229, 24, 61, 0.1);
    transform: translateY(-1px);
  }

  &--filled {
    border-style: solid;
    border-color: $br-border-soft;
    padding: 0;
  }
}

.product-form__dropzone-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $br-brand-soft;
  margin-bottom: $br-space-2;
}

.product-form__dropzone-title {
  margin: 0;
  font-size: $br-font-size-md;
  font-weight: 800;
  color: $br-ink-soft;
}

.product-form__dropzone-hint {
  margin: 2px 0 0;
  font-size: $br-font-size-xs;
  color: $br-muted;
  font-weight: 600;
}

.product-form__image {
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
}

.product-form__image-remove {
  position: absolute !important;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: $br-shadow-sm;
}

.product-form__file-input {
  display: none;
}

.product-form__preview {
  margin-top: $br-space-4;
}

.product-form__preview-label {
  margin: 0 0 $br-space-2;
  font-size: $br-font-size-xs;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.45px;
  color: $br-muted;
}

.product-form__preview-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: $br-space-4 $br-space-3;
  border-radius: $br-radius-xl;
  border: 1px solid $br-border-soft;
  background: $br-bg-elevated;
  box-shadow: $br-shadow-md;
}

.product-form__preview-thumb {
  width: 72px;
  height: 72px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $br-brand-soft;
  overflow: hidden;
  margin-bottom: $br-space-3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.product-form__preview-name {
  margin: 0 0 4px;
  font-size: $br-font-size-sm;
  font-weight: 700;
  color: $br-ink-soft;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-form__preview-price {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: $br-brand;
  font-variant-numeric: tabular-nums;
}

.product-form__preview-cashback {
  margin-top: $br-space-2;
  padding: 3px 10px;
  border-radius: $br-radius-pill;
  background: $br-success-soft;
  color: $br-success-dark;
  font-size: 11px;
  font-weight: 800;
}

.product-form__cancel {
  font-weight: 700 !important;
}

@media (min-width: 960px) {
  .product-form__switch-col,
  .product-form__slider-col {
    padding-top: 4px;
  }
}

@media (max-width: 1263px) {
  .product-form__aside {
    position: static;
  }
}
</style>
